import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { sortBy } from 'lodash';
import { Observable, switchMap } from 'rxjs';
import { ELoadStatus } from '../../../state/state.interface';
import { HttpUtilitiesService, IBaseResponse } from '../../../utilities/http-utilities.service';
import {
  IStepperDataResponse,
  IWorkInstructionStep,
  IWorkInstructionSubmission,
} from '../../settings/work-instructions/work-instruction-editor/work-instruction-steps/work-instruction-steps.interface';
import {
  IItemAssignedStepCreateDto,
  IItemAssignedStepUpdateDto,
  ISubmissionUpsertDto,
} from './work-instruction-stepper.interface';

export interface IWorkInstructionStepperState {
  assignmentLoading: ELoadStatus; // TODO bu kullaniliyor mu?
  dataLoading: ELoadStatus;
  steps: IWorkInstructionStep[];
  submissions: IWorkInstructionSubmission[];
  updateStatus: ELoadStatus;
}

@Injectable()
export class WorkInstructionStepperStore extends ComponentStore<IWorkInstructionStepperState> {
  public readonly steps: Signal<IWorkInstructionStep[]> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.steps,
  );
  public readonly submissions: Signal<IWorkInstructionSubmission[]> = this.selectSignal(
    (state: IWorkInstructionStepperState) => state.submissions,
  );

  public readonly updateStatus = this.selectSignal((state) => state.updateStatus);
  public readonly dataLoading = this.selectSignal((state) => state.dataLoading);

  public readonly combinedStatus = computed(() => {
    return this.httpUtilities.combineHttpStatusesIntoOne([this.dataLoading(), this.updateStatus()]);
  });
  public readonly submissions$ = this.select((state: IWorkInstructionStepperState) => state.submissions);

  readonly loadStepsAndSubmissions = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((itemWorkInstructionAssignmentId: number) => {
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getStepsAndSubmissions(itemWorkInstructionAssignmentId).pipe(
          tapResponse(
            (response: IBaseResponse<IStepperDataResponse>) =>
              this.patchState({ dataLoading: ELoadStatus.success, ...this.extractStepsAndSubmissions(response.data) }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly upsertWorkInstructionSubmission = this.effect((trigger$: Observable<ISubmissionUpsertDto>) =>
    trigger$.pipe(
      switchMap(({ submissionId, ...params }) => {
        this.patchState({ updateStatus: ELoadStatus.loading });

        return (
          submissionId
            ? this.updateOne(submissionId, params.dto)
            : this.createOne({ ...params.dto, itemGuideAssignmentId: params.guideAssignmentId, stepId: params.stepId })
        ).pipe(
          switchMap(() => {
            return this.getStepsAndSubmissions(params.guideAssignmentId).pipe(
              tapResponse(
                (response) =>
                  this.patchState({
                    updateStatus: ELoadStatus.success,
                    ...this.extractStepsAndSubmissions(response.data),
                  }),
                // eslint-disable-next-line no-console
                (error) => console.error('Error loading data', error),
              ),
            );
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      assignmentLoading: ELoadStatus.initial,
      dataLoading: ELoadStatus.initial,
      steps: [],
      submissions: [],
      updateStatus: ELoadStatus.initial,
    });
  }

  private extractStepsAndSubmissions(
    responseData: IStepperDataResponse,
  ): Pick<IWorkInstructionStepperState, 'submissions' | 'steps'> {
    return {
      steps: sortBy(responseData.guide.guideSteps, 'order').map((step: IWorkInstructionStep) => {
        return {
          ...step,
          submission: responseData.guideSubmissions.find(
            (submission: IWorkInstructionSubmission) => submission.stepId === step.id,
          ),
        };
      }),
      submissions: responseData.guideSubmissions,
    };
  }

  private getStepsAndSubmissions(
    itemWorkInstructionAssignmentId: number,
  ): Observable<IBaseResponse<IStepperDataResponse>> {
    const httpParams: HttpParams = new HttpParams()
      .set('fields', 'boardId')
      .append('join', 'guide')
      .append('join', 'guide.guideSteps')
      .append('join', 'guideSubmissions');

    return this.http.get<IBaseResponse<IStepperDataResponse>>(
      `item-guide-assignments/${itemWorkInstructionAssignmentId}`,
      {
        params: httpParams,
      },
    );
  }

  private updateOne(
    submissionId: number,
    dto: IItemAssignedStepUpdateDto,
  ): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.patch<IBaseResponse<IWorkInstructionStep>>(`guide-submission/${submissionId}`, dto);
  }

  private createOne(dto: IItemAssignedStepCreateDto): Observable<IBaseResponse<IWorkInstructionStep>> {
    return this.http.post<IBaseResponse<IWorkInstructionStep>>('guide-submission', dto);
  }
}
